import { resolveSpecialtyDemoTenantFromEmail } from "../demo/demo-tenants.catalog";

/** Domaine racine — dev local par défaut ; prod : itaas-suite.com via env. */
export const DEFAULT_PLATFORM_BASE_DOMAIN = "itaas.local";
export const PRODUCTION_PLATFORM_BASE_DOMAIN = "itaas-suite.com";

/** Sous-domaines réservés (Suite centrale, API, etc.) — pas des tenants. */
const RESERVED_SUBDOMAINS = new Set([
  "www",
  "app",
  "suite",
  "api",
  "admin",
  "iam",
  "login",
]);

function readNodeEnv(key: string): string | undefined {
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } })
    .process;
  const value = proc?.env?.[key]?.trim();
  return value ? value.toLowerCase() : undefined;
}

function readEnvDomain(): string | undefined {
  if (typeof import.meta !== "undefined") {
    const vite =
      (import.meta as ImportMeta & { env?: Record<string, string> }).env
        ?.VITE_ITAAS_PLATFORM_DOMAIN;
    if (vite?.trim()) return vite.trim().toLowerCase();
  }
  const node = readNodeEnv("ITAAS_PLATFORM_DOMAIN");
  if (node) return node;
  return undefined;
}

/** Domaine racine plateforme (ex. itaas-suite.com). */
export function getPlatformBaseDomain(): string {
  const fromEnv = readEnvDomain();
  if (fromEnv) return fromEnv;
  if (typeof import.meta !== "undefined") {
    const env = (import.meta as ImportMeta & { env?: { PROD?: boolean } }).env;
    if (env?.PROD) return PRODUCTION_PLATFORM_BASE_DOMAIN;
  }
  if (readNodeEnv("NODE_ENV") === "production") {
    return PRODUCTION_PLATFORM_BASE_DOMAIN;
  }
  return DEFAULT_PLATFORM_BASE_DOMAIN;
}

/** FQDN tenant : acme.itaas-suite.com */
export function buildTenantHostname(slug: string): string {
  const normalized = slug.trim().toLowerCase();
  return `${normalized}.${getPlatformBaseDomain()}`;
}

/** Email admin provisionné : admin@acme.itaas-suite.com */
export function buildTenantAdminLoginEmail(slug: string): string {
  return `admin@${buildTenantHostname(slug)}`;
}

function defaultProtocol(): "http" | "https" {
  const base = getPlatformBaseDomain();
  if (base === "itaas.local" || base === "localhost") return "http";
  return "https";
}

/** URL de connexion du tenant sur son sous-domaine. */
export function buildTenantLoginUrl(
  slug: string,
  path = "/login",
): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (typeof window !== "undefined") {
    const host = window.location.hostname.toLowerCase();
    if (
      getPlatformBaseDomain() === DEFAULT_PLATFORM_BASE_DOMAIN &&
      (host === "localhost" || host === "127.0.0.1")
    ) {
      return `${window.location.origin}${normalizedPath}`;
    }
  }

  const protocol = defaultProtocol();
  return `${protocol}://${buildTenantHostname(slug)}${normalizedPath}`;
}

/** Extrait le slug depuis un hostname (acme.itaas-suite.com → acme). */
export function resolveTenantSlugFromHostname(hostname: string): string | null {
  const normalized = hostname.trim().toLowerCase().split(":")[0] ?? "";
  if (!normalized || normalized === "localhost" || normalized === "127.0.0.1") {
    return null;
  }

  const base = getPlatformBaseDomain();
  const suffix = `.${base}`;
  if (!normalized.endsWith(suffix)) {
    if (base === DEFAULT_PLATFORM_BASE_DOMAIN && normalized.endsWith(".itaas.local")) {
      const sub = normalized.slice(0, -".itaas.local".length);
      if (sub && !RESERVED_SUBDOMAINS.has(sub)) return sub;
    }
    return null;
  }

  const subdomain = normalized.slice(0, -suffix.length);
  if (!subdomain || RESERVED_SUBDOMAINS.has(subdomain)) return null;
  return subdomain;
}

/** Comptes démo locaux — mapping via catalogue (core/demo). */

/** Extrait le slug IAM depuis l'email admin provisionné. */
export function resolveTenantSlugFromAdminEmail(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  const base = getPlatformBaseDomain().replace(/\./g, "\\.");

  const current = normalized.match(
    new RegExp(`^[^@]+@([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)\\.${base}$`),
  );
  if (current?.[1]) return current[1];

  const legacy = normalized.match(
    /^[^@]+@([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)\.itaas\.local$/,
  );
  if (legacy?.[1]) return legacy[1];

  // Emails admin essai PEP : admin@{slug}.itaas-suite.com (même si VITE_ITAAS_PLATFORM_DOMAIN absent au build)
  const prodAdmin = normalized.match(
    /^[^@]+@([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)\.itaas-suite\.com$/,
  );
  if (prodAdmin?.[1] && prodAdmin[1] !== "app" && prodAdmin[1] !== "www") {
    return prodAdmin[1];
  }

  const specialtyDemo = resolveSpecialtyDemoTenantFromEmail(normalized);
  if (specialtyDemo) return specialtyDemo;

  if (normalized.endsWith("@itaas.demo")) return "demo";
  if (normalized.endsWith("@itaas-rdc.com")) return "platform";
  return null;
}
